import React from 'react';
import { XMarkIcon, ArchiveBoxIcon, CubeIcon, MapPinIcon, DocumentTextIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { InventoryItem } from '../../services/api';

interface InventoryItemDetailProps {
  isOpen: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  onEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
}

const InventoryItemDetail: React.FC<InventoryItemDetailProps> = ({ isOpen, onClose, item, onEdit, onDelete }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'IN_STOCK':
        return 'bg-success-100 text-success-800';
      case 'LOW_STOCK':
        return 'bg-warning-100 text-warning-800';
      case 'OUT_OF_STOCK':
        return 'bg-error-100 text-error-800';
      case 'RESERVED':
        return 'bg-primary-100 text-primary-800';
      case 'DAMAGED':
        return 'bg-neutral-100 text-neutral-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-success-600 to-success-700">
          <div className="flex items-center">
            <ArchiveBoxIcon className="h-8 w-8 text-white mr-3" />
            <div>
              <h3 className="text-xl font-semibold text-white">{item.product?.name || 'Unknown Product'}</h3>
              <p className="text-sm text-success-100">Inventory Item #{item.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-success-100 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Product Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <CubeIcon className="h-5 w-5 mr-2 text-success-600" />
              Product Information
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Product Name</label>
                <p className="mt-1 text-sm text-neutral-900">{item.product?.name || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">SKU</label>
                <p className="mt-1 text-sm text-neutral-900">{item.product?.sku || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Category</label>
                <p className="mt-1 text-sm text-neutral-900">{item.product?.category?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Supplier</label>
                <p className="mt-1 text-sm text-neutral-900">{item.product?.supplier?.name || '-'}</p>
              </div>
              {item.product?.description && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-neutral-500">Description</label>
                  <p className="mt-1 text-sm text-neutral-900">{item.product.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Inventory Details */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <ArchiveBoxIcon className="h-5 w-5 mr-2 text-success-600" />
              Inventory Details
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Quantity</label>
                <p className="mt-1 text-lg font-semibold text-neutral-900">
                  {item.quantity} {item.unit || item.product?.unit || ''}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Unit</label>
                <p className="mt-1 text-sm text-neutral-900">{item.unit || item.product?.unit || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Status</label>
                <span className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                  {item.status.replace('_', ' ')}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Lot Number</label>
                <p className="mt-1 text-sm text-neutral-900">{item.lot?.lotNumber || item.batchNumber || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Expiry Date</label>
                <p className="mt-1 text-sm text-neutral-900">
                  {item.lot?.expiryDate ? new Date(item.lot.expiryDate).toLocaleDateString() : item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                </p>
              </div>
              {item.lot?.supplier && (
                <div>
                  <label className="block text-sm font-medium text-neutral-500">Lot Supplier</label>
                  <p className="mt-1 text-sm text-neutral-900">{item.lot.supplier.name}</p>
                </div>
              )}
              {item.lot?.receivedDate && (
                <div>
                  <label className="block text-sm font-medium text-neutral-500">Received Date</label>
                  <p className="mt-1 text-sm text-neutral-900">{new Date(item.lot.receivedDate).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Location Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-success-600" />
              Location
            </h4>
            {item.tray ? (
              <div className="bg-neutral-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Warehouse</label>
                    <p className="mt-1 text-sm text-neutral-900">
                      {item.tray.bin?.shelf?.warehouse?.name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Warehouse Code</label>
                    <p className="mt-1 text-sm text-neutral-900">
                      {item.tray.bin?.shelf?.warehouse?.code || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Shelf</label>
                    <p className="mt-1 text-sm text-neutral-900">
                      {item.tray.bin?.shelf?.code || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Bin</label>
                    <p className="mt-1 text-sm text-neutral-900">
                      {item.tray.bin?.code || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Tray</label>
                    <p className="mt-1 text-sm text-neutral-900">{item.tray.code}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-500">Full Location Path</label>
                    <p className="mt-1 text-sm font-medium text-success-600">
                      {item.tray.bin?.shelf?.warehouse?.name} → {item.tray.bin?.shelf?.code} → {item.tray.bin?.code} → {item.tray.code}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 bg-neutral-50 rounded-lg">
                <MapPinIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <p className="mt-2 text-sm text-neutral-500">No location assigned</p>
              </div>
            )}
          </div>

          {/* Notes */}
          {item.notes && (
            <div>
              <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-success-600" />
                Notes
              </h4>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <p className="text-sm text-neutral-900 whitespace-pre-wrap">{item.notes}</p>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm text-neutral-500">
              <div>
                <span className="font-medium">Created:</span> {new Date(item.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="font-medium">Updated:</span> {new Date(item.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse justify-end gap-3 p-6 border-t bg-neutral-50 sm:flex-row">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => onEdit(item)}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-200 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
          >
            <PencilIcon className="h-4 w-4" />
            Edit item
          </button>
          <button
            onClick={() => onDelete(item)}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-error-200 px-4 py-2 text-sm font-medium text-error-700 hover:bg-error-50"
          >
            <TrashIcon className="h-4 w-4" />
            Delete item
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryItemDetail;
